import { serviceInstance } from '../../../../axiosinstance';
import { getSession } from '../../../../getSession';

const getRequestEnvelope = (details) => {
  const { TokenId, userId } = getSession();
  return {
    Token: TokenId,
    LoggedUserId: userId || 0,
    Message: '',
    MAC_Address: '',
    IP_Address: '',
    Details: details
  };
};

const getDataDetails = (response) => {
  const body = response?.data ?? response;
  return body?.Details ?? body?.d ?? body;
};

export const resolveLeadId = (lead) => {
  const value = lead?.ID ?? lead?.LeadId ?? lead?.Id ?? lead?.id ?? lead?.LeadID ?? null;
  if (value === null || value === undefined || value === '') return null;
  return String(value);
};

export const fetchCustomButtonList = async (moduleName = 'Leads') => {
  const { userId } = getSession();
  const details = {
    UserId: userId || 0,
    ModuleName: moduleName
  };

  const response = await serviceInstance.post('UserCRM/getCustomButtonList', getRequestEnvelope(details));
  const list = getDataDetails(response);
  return Array.isArray(list) ? list : [];
};

export const fetchLeadHeaderPlaceholders = async () => {
  const response = await serviceInstance.post('UserCRM/getLeadHeaderPlaceholders', getRequestEnvelope(''));
  const details = getDataDetails(response);
  return Array.isArray(details) ? details : [];
};

export const fetchLeadPlaceholderValues = async (placeholders, selectedIds) => {
  const { userId } = getSession();
  const normalized = Array.isArray(placeholders)
    ? placeholders
    : String(placeholders || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

  const details = {
    placeholders: normalized.join(','),
    leadIDs: Array.isArray(selectedIds) ? selectedIds : [selectedIds],
    userID: userId || 0
  };

  const response = await serviceInstance.post('UserCRM/GetLeadDetails', getRequestEnvelope(details));
  const payload = getDataDetails(response);

  if (!Array.isArray(payload)) return {};

  const combinedValues = {};
  payload.forEach((inner) => {
    if (!Array.isArray(inner)) return;
    inner.forEach((item) => {
      if (!item?.key) return;
      const current = combinedValues[item.key];
      if (current) {
        combinedValues[item.key] = `${current},${item.value ?? ''}`;
      } else {
        combinedValues[item.key] = item.value ?? '';
      }
    });
  });

  return combinedValues;
};

export const checkWebhookUrl = async ({ buttonId, webhookURL, batchId = '' }) => {
  const { userId } = getSession();
  const details = {
    ButtonId: buttonId,
    UserId: userId || 0,
    WebhookURL: webhookURL,
    BatchId: batchId || ''
  };

  const response = await serviceInstance.post('UserCRM/getCheckWebhookURL', getRequestEnvelope(details));
  const payload = getDataDetails(response);
  return Array.isArray(payload) ? payload : [];
};

export const updateWebhookParams = async ({
  webhookId,
  params,
  leadIds = [],
  batchId = '',
  headerKey = '',
  headerValue = '',
  senderId = ''
}) => {
  const { userId } = getSession();
  const isBatch = Boolean(String(batchId || '').trim());

  const details = {
    WebhookID: webhookId,
    Params: JSON.stringify(params || {}),
    UserID: userId || 0,
    HeaderKey: headerKey,
    HeaderValue: headerValue,
    SenderId: senderId
  };

  if (isBatch) {
    details.BatchId = String(batchId);
  } else {
    details.LeadIDs = Array.isArray(leadIds) ? leadIds : [];
  }

  const endpoint = isBatch ? 'UserCRM/updateWebhookParamsBatch' : 'UserCRM/updateWebhookParams';
  const response = await serviceInstance.post(endpoint, getRequestEnvelope(details));
  return getDataDetails(response);
};

export const extractPlaceholders = (url) => {
  const regex = /#\{lead\.([^}]+)\}/g;
  const placeholders = new Set();
  let match = regex.exec(url || '');
  while (match) {
    placeholders.add(match[1]);
    match = regex.exec(url || '');
  }
  return Array.from(placeholders);
};

export const replacePlaceholders = (url, combinedValues) => {
  let next = String(url || '');
  Object.entries(combinedValues || {}).forEach(([key, value]) => {
    const token = `#{lead.${key}}`;
    const safeValue = decodeURIComponent(String(value ?? ''));
    next = next.split(token).join(safeValue);
  });
  return next;
};

export const invokeUrlByDisposition = (url, disposition) => {
  if (!url) return;
  const mode = String(disposition || 'Existing Tab');
  if (mode === 'New Tab') {
    window.open(url, '_blank');
    return;
  }

  if (mode === 'New Window') {
    window.open(url, '_blank', 'toolbar=yes,scrollbars=yes,resizable=yes,width=800,height=600');
    return;
  }

  window.location.href = url;
};

export const buildInvokableUrl = async (url, selectedIds) => {
  const placeholders = extractPlaceholders(url);
  if (!placeholders.length) return url;

  const values = await fetchLeadPlaceholderValues(placeholders, selectedIds);
  const replaced = replacePlaceholders(url, values);
  const cleaned = replaced
    .replace(/([?&])[^=&]+=&/g, '$1')
    .replace(/([?&])[^=&]+=$/, '')
    .replace(/[?&]$/, '');

  return cleaned;
};

export const parseWebhookParamConfig = (rawWebhookDetails) => {
  if (!rawWebhookDetails) return { webhookId: '', requiredParams: {} };

  const paramsRaw = rawWebhookDetails?.Params;
  let parsed = [];
  if (Array.isArray(paramsRaw)) {
    parsed = paramsRaw;
  } else if (typeof paramsRaw === 'string' && paramsRaw.trim()) {
    try {
      parsed = JSON.parse(paramsRaw);
    } catch (error) {
      parsed = [];
    }
  }

  const requiredParams = {};
  if (Array.isArray(parsed)) {
    parsed.forEach((item) => {
      if (!item?.key) return;
      if (item.value === '') {
        requiredParams[item.key] = '';
      }
    });
  }

  return {
    webhookId: rawWebhookDetails?.WebhookID || rawWebhookDetails?.WebhookId || '',
    requiredParams
  };
};

export const validateSenderID = (value) => {
  const text = String(value || '').trim();
  if (!text) return true;
  const isNumeric = /^\d+$/.test(text);
  const isAlphabetic = /^[a-zA-Z]+$/.test(text);
  if (text.length < 8 || text.length > 11) return false;
  if (!isNumeric && !isAlphabetic) return false;
  return true;
};
